import React, { useState, useEffect } from 'react';
import { Button, Form, Upload, Modal, App, Input, Space } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import styles from './ProductDetailTemplate4.module.scss';
import type { UploadFile, UploadProps } from 'antd';
import { useAppSelector } from '@/store/hooks';
import icon1 from '@/assets/images/templete4.jpg';
import { useTranslation } from 'react-i18next';

interface ImageUrl {
  url: string;
  name: string;
}

interface ProductItem {
  title: string;
  content1: string;
  content2: string;
  imgUrl: ImageUrl[];
}

export interface ProductDetailTemplate4Props {
  data: {
    name: string;
    contentList: ProductItem[];
  };
  onChange?: (data: any, selfIndex: number) => void;
  editable?: boolean;
  selfIndex?: number;
  thumbnailMode?: boolean;//是否是缩略图模式
  onDelete?: (selfIndex: number) => void;
}

const ProductDetailTemplate4: React.FC<ProductDetailTemplate4Props> = ({
  data,
  onChange,
  editable = true,
  selfIndex = 0,
  thumbnailMode = false,
  onDelete
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const { t } = useTranslation();
  // 预览状态
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  // 编辑表单数据
  const [contentList, setContentList] = useState<ProductItem[]>(data?.contentList || []);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // 获取token
  const token = useAppSelector((state) => state.user.token);

  // 初始化数据
  useEffect(() => {
    if (data) {
      setContentList(data.contentList || []);
    }
  }, [data]);

  // 图片上传配置
  const uploadProps: UploadProps = {
    name: 'file',
    action: `${process.env.REACT_APP_API_URL}/api/product/save_upload_file`,
    accept: ".png,.jpeg,.jpg,.gif",
    headers: {
      token: `${token}`
    },
    listType: 'picture-card',
    fileList: fileList,
    beforeUpload: (file) => {
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t('showCase.Image10MB'));
      }
      return isLt10M; // 返回 false 阻止上传
    },
    onChange(info) {
      // 复制一份文件列表
      const newFileList = [...info.fileList];

      // 对文件状态进行处理
      newFileList.forEach(file => {
        if (file.response) {
          file.url = file?.response?.data?.url;
        }
      });

      // 更新状态
      setFileList(newFileList);

      if (info.file.status === 'done') {
        message.success(`${info.file.name} ${t('showCase.uploadedsuccessfully')}`);
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} ${t('showCase.uploadfailed')}`);
      }
    },
    onPreview: async (file) => {
      let src = file.url as string;
      if (!src) {
        src = await new Promise(resolve => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj as any);
          reader.onload = () => resolve(reader.result as string);
        });
      }
      // 使用 Modal 预览而不是 window.open 来避免跨域问题
      setPreviewImage(src);
      setPreviewVisible(true);
    }
  };

  // 打开编辑模式
  const handleEdit = () => {
    setIsEditing(true);
  };

  // 打开特定项目的编辑
  const handleEditItem = (index: number) => {
    setEditingItemIndex(index);
    const currentItem = contentList[index];
    
    // 设置图片列表
    if (currentItem.imgUrl && currentItem.imgUrl.length > 0) {
      const images = currentItem.imgUrl.map((item, idx) => ({
        uid: `-${idx}`,
        name: item.name || `image-${idx}`,
        status: 'done' as const,
        url: item.url
      }));
      setFileList(images);
    } else {
      setFileList([]);
    }

    form.setFieldsValue({
      title: currentItem.title,
      content1: currentItem.content1,
      content2: currentItem.content2
    });
  };

  // 保存编辑
  const handleSaveItem = () => {
    if (editingItemIndex === null) return;

    form.validateFields().then(values => {
      const newContentList = [...contentList];
      newContentList[editingItemIndex] = {
        ...newContentList[editingItemIndex],
        title: values.title,
        content1: values.content1,
        content2: values.content2,
        imgUrl: fileList.map(file => ({
          url: file.url || '',
          name: file.name || ''
        }))
      };

      const updatedData = {
        ...data,
        contentList: newContentList
      };

      onChange && onChange(updatedData, selfIndex);
      setContentList(newContentList);
      setEditingItemIndex(null);
      message.success(t('showCase.Itemupdatedsuccessfully'));
    });
  };

  // 取消编辑
  const handleCancelItem = () => {
    setEditingItemIndex(null);
    setFileList([]);
  };

  // 添加内容项
  const handleAddItem = () => {
    const newItem: ProductItem = {
      title: 'New Product',
      content1: 'Dimensions',
      content2: 'Material',
      imgUrl: []
    };

    const newContentList = [...contentList, newItem];
    const updatedData = {
      ...data,
      contentList: newContentList
    };

    onChange && onChange(updatedData, selfIndex);
    setContentList(newContentList);
    message.success(t('showCase.Newitemadded'));
  };

  // 删除内容项
  const handleDeleteItem = (index: number) => {
    if (contentList.length <= 1) {
      message.warning(t('showCase.Atleastrequired'));
      return;
    }

    const newContentList = contentList.filter((_, i) => i !== index);
    const updatedData = {
      ...data,
      contentList: newContentList
    };

    onChange && onChange(updatedData, selfIndex);
    setContentList(newContentList);
    message.success(t('showCase.Itemdeleted'));
  };

  // 上传按钮
  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t('showCase.Upload')}</div>
    </div>
  );

  return (
    <div className={styles['template-container']} style={{padding:editable? '0px':'10px'}}>

      {thumbnailMode ? (
        <div className={styles['template-thumbnail']} onClick={handleEdit}>
          <div className={styles['thumbnail-number']}>
            {selfIndex + 1}
          </div>
          <img className={styles['thumbnail-image']} src={icon1 || ''} alt="" />
          <div className={styles['thumbnail-actions']}>
            <Button 
              size="small"
              danger
              className={styles['delete-btn']}
              onClick={(e) => {
                e.stopPropagation();
                // 这里需要调用删除函数，但需要从父组件传入
                onDelete && onDelete(selfIndex);
              }}
            >
              {t('showCase.Delete')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles['template-content']}>
          <div className={styles['products-grid']}>
            {contentList.map((item, index) => (
              <div key={index} className={styles['product-item']}>
                <div className={styles['product-image']}>
                  {item.imgUrl && item.imgUrl.length > 0 && (
                    <img
                      src={item.imgUrl[0].url}
                      alt={item.title}
                      className={styles['item-image']}
                    />
                  )}
                </div>
                <div className={styles['product-details']}>
                  <div className={styles['product-title']}>{item.title}</div>
                  <div className={styles['product-spec']}>{item.content1}</div>
                  <div className={styles['product-material']}>{item.content2}</div>
                </div>
                {isEditing && (
                  <div className={styles['item-actions']}>
                    <Button 
                      type="primary" 
                      size="small" 
                      icon={<EditOutlined />} 
                      onClick={() => handleEditItem(index)}
                    >
                      {t('showCase.Edit')}
                    </Button>
                    <Button 
                      danger 
                      size="small" 
                      icon={<DeleteOutlined />} 
                      onClick={() => handleDeleteItem(index)}
                    >
                      {t('showCase.Delete')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {isEditing && (
            <div className={styles['add-item-container']}>
              <Button 
                type="dashed" 
                onClick={handleAddItem} 
                icon={<PlusOutlined />}
              >
                {t('showCase.AddProductItem')}
              </Button>
            </div>
          )}
        </div>
      )}
      

      {/* 编辑单个项目的模态框 */}
      {editingItemIndex !== null && (
        <Modal
          title="Edit Product Item"
          open={editingItemIndex !== null}
          onOk={handleSaveItem}
          onCancel={handleCancelItem}
          okText="Save"
          cancelText="Cancel"
          className={styles['edit-modal']}
          width={700}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              label="Product Title"
              name="title"
              rules={[{ required: true, message: 'Please enter product title' }]}
            >
              <Input placeholder="Enter product title" maxLength={50} />
            </Form.Item>

            <Form.Item
              label="Dimensions"
              name="content1"
              rules={[{ required: true, message: 'Please enter dimensions' }]}
            >
              <Input placeholder="Enter dimensions (e.g. 13.78*9.84*5.91)" />
            </Form.Item>

            <Form.Item
              label="Material"
              name="content2"
              rules={[{ required: true, message: 'Please enter material' }]}
            >
              <Input placeholder="Enter material (e.g. Natural Bamboo Strips)" />
            </Form.Item>

            <Form.Item
              label="Product Image"
            >
              <Upload {...uploadProps}>
                {fileList.length >= 1 ? null : uploadButton}
              </Upload>
              <div className={styles['upload-hint']}>
                Please upload a high-quality image (Recommended size: 300x300px)
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* 图片预览 Modal */}
      <Modal
        open={previewVisible}
        title="Image Preview"
        footer={null}
        width={'80%'}
        onCancel={() => setPreviewVisible(false)}
        zIndex={2000} 
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default ProductDetailTemplate4; 